import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import { Badge } from "@/common/components/ui/badge";
import {
  TrendingUp,
  TrendingDown,
  Download,
  ShoppingCart,
  Clock,
  ChevronRight,
  PieChart,
  Wallet,
  Calendar,
  AlertCircle,
  Scale,
  IndianRupee,
  History,
} from "lucide-react";
import { Link } from "react-router-dom";
import { GoldService } from "@/services/APIs";

const GoldDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    // setTitle("Physical Gold Operations"); // Assuming setTitle is not available in LayoutContext or handled differently
    const fetchDashboardData = async () => {
      try {
        // Using the service we created
        // Since getDashboard returns Promise<any>, we can await it
        const response = await GoldService.getDashboard();
        setData(response);
      } catch (error) {
        console.error("Error fetching gold dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  const metrics = data?.metrics ?? {
    total_orders_today: 0,
    total_orders_mtd: 0,
    pending_orders: 0,
    processing_orders: 0,
    delivered_orders: 0,
    cancelled_orders: 0,
    total_gold_sold: 0,
    total_order_value: 0,
    total_commission: 0,
    pending_settlement: 0,
  };

  const statsCards = [
    {
      title: "Orders (Today)",
      value: metrics.total_orders_today,
      change: "+12%",
      positive: true,
      icon: ShoppingCart,
      color: "bg-icon-blue text-primary",
    },
    {
      title: "Gold Sold (grams)",
      value: metrics.total_gold_sold.toFixed(2),
      change: "+8.5%",
      positive: true,
      icon: Scale,
      color: "bg-icon-orange text-secondary",
    },
    {
      title: "Revenue (MTD)",
      value: `₹${(metrics.total_order_value / 100000).toFixed(2)}L`,
      change: "+15.2%",
      positive: true,
      icon: IndianRupee,
      color: "bg-icon-teal text-primary",
    },
    {
      title: "Commission",
      value: `₹${metrics.total_commission.toLocaleString()}`,
      change: "+10.1%",
      positive: true,
      icon: Wallet,
      color: "bg-icon-purple text-secondary",
    },
    {
      title: "Pending Orders",
      value: metrics.pending_orders,
      change: "-2",
      positive: true,
      icon: Clock,
      color: "bg-icon-orange text-secondary",
    },
    {
      title: "Pending Settlement",
      value: `₹${metrics.pending_settlement.toLocaleString()}`,
      change: "0%",
      positive: true,
      icon: History,
      color: "bg-icon-red text-destructive",
    },
  ];

  const quickActions = [
    {
      label: "Update Live Rate",
      href: "/admin/physical-gold/price-control",
      icon: TrendingUp,
      color: "bg-icon-orange text-secondary",
    },
    {
      label: "Manage Orders",
      href: "/admin/physical-gold/orders",
      icon: ShoppingCart,
      color: "bg-primary/10 text-primary",
    },
    {
      label: "Settlements",
      href: "/admin/physical-gold/settlements",
      icon: IndianRupee,
      color: "bg-icon-teal text-primary",
    },
    {
      label: "Operation Logs",
      href: "/admin/physical-gold/logs",
      icon: History,
      color: "bg-icon-purple text-secondary",
    },
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Header Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 text-white md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative z-10 flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <p className="mb-1 text-sm font-medium text-white/60">
              MMTC Physical Gold Module
            </p>
            <h1 className="font-display text-2xl font-bold md:text-3xl">
              Gold Operations Hub
            </h1>
            <p className="mt-1 text-white/70">
              Real-time inventory, pricing and order management
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
            >
              <Calendar className="h-4 w-4" /> Last 30 Days
            </Button>
            <Button className="gap-2 bg-white text-secondary hover:bg-white/90">
              <Download className="h-4 w-4" /> Export Stats
            </Button>
          </div>
        </div>
        <div className="absolute right-0 top-0 h-64 w-64 -translate-y-1/2 translate-x-1/2 rounded-full bg-amber-500/10 blur-3xl" />
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        {statsCards.map(stat => (
          <Card
            key={stat.title}
            className="group overflow-hidden border-none shadow-sm transition-all hover:shadow-md"
          >
            <CardContent className="relative p-4">
              <div className="mb-3 flex items-start justify-between">
                <div
                  className={`rounded-xl p-2 ${stat.color} shadow-sm transition-transform group-hover:scale-110`}
                >
                  <stat.icon className="h-5 w-5" />
                </div>
                <span
                  className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${stat.positive ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}
                >
                  {stat.positive ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {stat.change}
                </span>
              </div>
              <p className="text-xl font-bold text-secondary">{stat.value}</p>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {stat.title}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {quickActions.map(action => (
          <Link to={action.href} key={action.label}>
            <Card className="group cursor-pointer border-gray-100 transition-all hover:border-primary/30 hover:shadow-md">
              <CardContent className="flex items-center gap-3 p-4">
                <div
                  className={`rounded-xl p-3 ${action.color} transition-transform group-hover:scale-110`}
                >
                  <action.icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-secondary">
                    {action.label}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent Orders Table */}
        <Card className="border-none shadow-sm lg:col-span-2">
          <CardHeader className="border-b pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-6 w-1.5 rounded-full bg-primary" />
                <CardTitle className="text-lg font-bold text-secondary">
                  Recent Gold Orders
                </CardTitle>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin/physical-gold/orders" className="gap-1">
                  View All <ChevronRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50/50">
                  <tr className="border-b">
                    <th className="p-4 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      Order ID
                    </th>
                    <th className="p-4 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      Customer
                    </th>
                    <th className="p-4 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      Gold Weight
                    </th>
                    <th className="p-4 text-right text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      Amount
                    </th>
                    <th className="p-4 text-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.recent_orders ?? []).map((order: any) => (
                    <tr
                      key={order.order_id}
                      className="group border-b transition-colors hover:bg-primary/5"
                    >
                      <td className="p-4 font-bold text-secondary">
                        {order.order_id}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="font-medium text-secondary">
                            {order.user_name}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            ID: {order.user_id}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge
                          variant="outline"
                          className="border-amber-200 bg-amber-50 font-bold text-amber-700"
                        >
                          {order.weight}g
                        </Badge>
                      </td>
                      <td className="p-4 text-right font-bold text-secondary">
                        ₹{order.amount.toLocaleString()}
                      </td>
                      <td className="p-4 text-center">
                        <Badge
                          className={`text-[10px] font-bold uppercase tracking-widest ${
                            order.order_status === "DELIVERED"
                              ? "bg-green-100 text-green-700 hover:bg-green-100"
                              : order.order_status === "CANCELLED"
                                ? "bg-red-100 text-red-700 hover:bg-red-100"
                                : "bg-blue-100 text-blue-700 hover:bg-blue-100"
                          }`}
                        >
                          {order.order_status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {(!data?.recent_orders ||
                    data.recent_orders.length === 0) && (
                    <tr>
                      <td
                        colSpan={5}
                        className="p-12 text-center italic text-muted-foreground"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <ShoppingCart className="h-12 w-12 opacity-10" />
                          <p>No orders recorded in the system yet</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Status Breakdown / Summary */}
        <Card className="border-none shadow-sm">
          <CardHeader className="border-b pb-2">
            <CardTitle className="flex items-center gap-2 text-lg font-bold text-secondary">
              <PieChart className="h-5 w-5 text-primary" />
              Order Flow Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[
                {
                  label: "Pending Verification",
                  count: metrics.pending_orders,
                  color: "bg-blue-500",
                },
                {
                  label: "In Process",
                  count: metrics.processing_orders,
                  color: "bg-amber-500",
                },
                {
                  label: "Success / Delivered",
                  count: metrics.delivered_orders,
                  color: "bg-green-500",
                },
                {
                  label: "Cancelled / Failed",
                  count: metrics.cancelled_orders,
                  color: "bg-red-500",
                },
              ].map(item => (
                <div key={item.label} className="flex flex-col gap-1.5">
                  <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    <span>{item.label}</span>
                    <span className="text-secondary">{item.count}</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full ${item.color} transition-all duration-1000`}
                      style={{
                        width:
                          metrics.total_orders_mtd > 0
                            ? `${(item.count / metrics.total_orders_mtd) * 100}%`
                            : "0%",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-xl border border-primary/10 bg-primary/5 p-4">
              <div className="mb-2 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-primary" />
                <span className="text-sm font-bold text-secondary">
                  Operations Alert
                </span>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Gold prices were last updated today at 10:30 AM. Ensure the live
                rate is locked before MMTC settlements are initiated.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GoldDashboard;
